import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '../auth/authStore';
import { makeApi } from '../../lib/api';

/**
 * Phase 7.5 — Guided "first Space" wizard.
 *
 * Closes FEATURES.md:205 — "Tenant onboarding flow: New signup →
 * wizard → first Space created". The path goes RegisterForm →
 * CreateFirstSpaceForm → THIS wizard, which takes over AFTER the
 * Space exists but BEFORE the operator has any Stories. (A legacy
 * single-page OnboardForm existed in parallel until 2026-05-30 —
 * it was unreferenced + English-only, deleted as part of the
 * translation-auditor follow-up.)
 *
 * Three steps:
 *
 *   1. Welcome — explains what a Space is, what they'll do next.
 *   2. Demo content — "yes please seed" / "I want to start blank".
 *      If yes, calls the DemoTenantSeeder-equivalent for this Space
 *      and creates a few sample stories using the starter blueprints.
 *   3. First story — slug + title input; creates a hero-block Story
 *      and hands off to the StoryEditor.
 *
 * Surfaces only when the current Space has 0 Stories. As soon as a
 * Story exists the wizard self-dismisses and the editor takes over.
 *
 * Mounted from EditorShell when view==='stories' && stories.length===0.
 */
interface Props {
  onStoryCreated: (uuid: string) => void;
}

type Step = 'welcome' | 'demo' | 'first-story';

export function FirstSpaceWizard({ onStoryCreated }: Props): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('welcome');
  const [slug, setSlug] = useState('welcome');
  const [title, setTitle] = useState('Willkommen');
  const [seedingDemo, setSeedingDemo] = useState(false);

  // Confirm the Space is genuinely empty before showing the wizard
  // — otherwise we'd flash it briefly on a loaded editor too.
  const storiesQuery = useQuery({
    enabled: token !== null && spaceSlug !== null,
    queryKey: ['stories', spaceSlug, 'wizard-check'],
    queryFn: async () => {
      if (token === null || spaceSlug === null) throw new Error('no auth');
      const api = makeApi(token);
      const result = await api.listStories(spaceSlug);
      if (result.status !== 'ok') throw new Error('listStories failed');
      return result.data.data;
    },
    staleTime: 5_000,
  });

  const createStoryMutation = useMutation({
    mutationFn: async (payload: { slug: string; title: string }) => {
      if (token === null || spaceSlug === null) throw new Error('no auth');
      const api = makeApi(token);
      // createStory takes content inline — no separate saveVersion
      // call needed for the first version.
      const created = await api.createStory(spaceSlug, {
        slug: payload.slug,
        lang: 'de-AT',
        content: {
          blocks: [
            {
              _component: 'hero',
              headline: payload.title,
              lead: 'Diese Story wurde gerade vom Onboarding-Wizard erstellt. Editiere sie und klick "Publish".',
            },
          ],
        },
        message: 'Wizard-created first story',
      });
      if (created.status !== 'ok') throw new Error('createStory failed: ' + created.status);
      return created.data.story.uuid;
    },
    onSuccess: (uuid) => {
      queryClient.invalidateQueries({ queryKey: ['stories', spaceSlug] });
      onStoryCreated(uuid);
    },
  });

  const seedDemoMutation = useMutation({
    mutationFn: async () => {
      if (token === null || spaceSlug === null) throw new Error('no auth');
      const api = makeApi(token);
      // Create three sample stories, each with a different blueprint
      // signature, so the operator gets a feel for what each block
      // type looks like rendered.
      const samples = [
        {
          slug: 'home',
          blocks: [
            { _component: 'hero', headline: 'Hallo Welt!', lead: 'Erste Demo-Story aus dem Wizard.' },
          ],
        },
        {
          slug: 'about',
          blocks: [
            { _component: 'hero', headline: 'Über uns', lead: 'Die zweite Demo-Story.' },
            { _component: 'quote', text: 'Ein gutes CMS bleibt aus dem Weg.', attribution: 'Peacock-Demo' },
          ],
        },
        {
          slug: 'faq',
          blocks: [
            {
              _component: 'faq',
              items: [
                { question: 'Was ist das hier?', answer: 'Demo-Inhalt vom Onboarding-Wizard.' },
                { question: 'Kann ich das löschen?', answer: 'Ja, jederzeit — Demo-Stories sind regulär bearbeitbar.' },
              ],
            },
          ],
        },
      ];
      const createdUuids: string[] = [];
      for (const sample of samples) {
        const created = await api.createStory(spaceSlug, {
          slug: sample.slug,
          lang: 'de-AT',
          content: { blocks: sample.blocks },
          message: 'Wizard demo seed',
        });
        if (created.status !== 'ok') continue; // skip silently — duplicate slug etc.
        createdUuids.push(created.data.story.uuid);
      }
      return createdUuids;
    },
    onSuccess: (uuids) => {
      queryClient.invalidateQueries({ queryKey: ['stories', spaceSlug] });
      const first = uuids[0];
      if (first !== undefined) onStoryCreated(first);
    },
  });

  // Self-dismiss when stories already exist (e.g. someone in another
  // tab created one). Returns an empty fragment; EditorShell still
  // renders the regular StoryEditor underneath.
  if (storiesQuery.data && storiesQuery.data.length > 0) {
    return <></>;
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <header className="mb-8 flex items-center gap-3">
        <div className="grid h-8 w-8 place-items-center rounded-full bg-teal-100 text-sm font-bold text-teal-700">
          {step === 'welcome' ? '1' : step === 'demo' ? '2' : '3'}
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            Onboarding · Schritt {step === 'welcome' ? '1' : step === 'demo' ? '2' : '3'} von 3
          </p>
          <h1 className="text-xl font-semibold">
            {step === 'welcome' && 'Willkommen in deinem neuen Space.'}
            {step === 'demo' && 'Demo-Inhalt zum Loslegen?'}
            {step === 'first-story' && 'Deine erste Story.'}
          </h1>
        </div>
      </header>

      {step === 'welcome' && (
        <div data-testid="wizard-step-welcome" className="space-y-4 text-sm">
          <p>
            Ein <strong>Space</strong> ist ein Container für deinen Content — Stories, Assets,
            Versionen, Locales. Der Space, in dem du gerade bist, ist leer; das ändern wir jetzt.
          </p>
          <p>
            Die nächsten zwei Schritte fragen, ob du mit Demo-Inhalten starten willst (gut zum
            Erkunden) oder direkt mit deiner ersten Story (gut, wenn du schon weißt, was du baust).
          </p>
          <p className="rounded border border-blue-200 bg-blue-50/60 p-3 text-xs">
            Wenn du das hier schließt, springt der Wizard wieder rein, sobald der Space leer
            bleibt — sobald du eine Story hast, siehst du nur noch den regulären Editor.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              data-testid="wizard-welcome-next"
              onClick={() => setStep('demo')}
              className="rounded bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
            >
              Weiter
            </button>
          </div>
        </div>
      )}

      {step === 'demo' && (
        <div data-testid="wizard-step-demo" className="space-y-4 text-sm">
          <p>Wir können dir drei beispielhafte Stories anlegen (<code>/home</code>, <code>/about</code>,
            <code>/faq</code>), die zeigen wie Hero, Quote und FAQ-Blocks aussehen.</p>
          <div className="grid gap-2">
            <button
              type="button"
              data-testid="wizard-demo-yes"
              disabled={seedingDemo || seedDemoMutation.isPending}
              onClick={async () => {
                setSeedingDemo(true);
                try {
                  await seedDemoMutation.mutateAsync();
                } finally {
                  setSeedingDemo(false);
                }
              }}
              className="rounded border border-teal-600 bg-white px-4 py-2 text-left text-sm text-teal-700 hover:bg-teal-50"
            >
              <strong className="block">Ja, leg drei Demo-Stories an</strong>
              <span className="text-xs text-zinc-500">In ~2 Sekunden landest du im Editor mit der ersten geöffnet.</span>
            </button>
            <button
              type="button"
              data-testid="wizard-demo-no"
              disabled={seedingDemo}
              onClick={() => setStep('first-story')}
              className="rounded border border-zinc-300 bg-white px-4 py-2 text-left text-sm hover:bg-zinc-50"
            >
              <strong className="block">Nein, ich starte selbst</strong>
              <span className="text-xs text-zinc-500">Nächster Schritt: Slug + Titel für deine erste Story.</span>
            </button>
          </div>
          {seedDemoMutation.isError && (
            <p className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              Demo-Seed gescheitert: {String((seedDemoMutation.error as Error)?.message ?? 'Unbekannter Fehler')}
            </p>
          )}
        </div>
      )}

      {step === 'first-story' && (
        <div data-testid="wizard-step-first-story" className="space-y-4 text-sm">
          <p>Wir legen eine erste Story mit einem Hero-Block an — du kannst sie sofort
            im Editor anpassen.</p>
          <label className="block">
            <span className="block pb-1 text-xs uppercase tracking-wide text-zinc-500">Slug (URL-Pfad)</span>
            <input
              data-testid="wizard-slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value.replace(/[^a-z0-9-]/g, ''))}
              className="w-full rounded border border-zinc-300 px-3 py-2 text-sm"
              placeholder="welcome"
            />
          </label>
          <label className="block">
            <span className="block pb-1 text-xs uppercase tracking-wide text-zinc-500">Titel</span>
            <input
              data-testid="wizard-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded border border-zinc-300 px-3 py-2 text-sm"
              placeholder="Willkommen"
            />
          </label>
          {createStoryMutation.isError && (
            <p className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              Story-Erstellung gescheitert: {String((createStoryMutation.error as Error)?.message ?? 'Unbekannter Fehler')}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setStep('demo')}
              className="rounded border border-zinc-300 bg-white px-4 py-2 text-sm hover:bg-zinc-50"
            >
              Zurück
            </button>
            <button
              type="button"
              data-testid="wizard-create-story"
              disabled={createStoryMutation.isPending || !slug.trim() || !title.trim()}
              onClick={() => createStoryMutation.mutate({ slug: slug.trim(), title: title.trim() })}
              className="rounded bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {createStoryMutation.isPending ? 'Wird erstellt…' : 'Story erstellen + öffnen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
